import { useState } from 'react';
import type { AdminProxy, AdminProxyTestResult, ProxyableSource } from '@chefty/shared-types';
import { PROXYABLE_SOURCES } from '@chefty/shared-types';
import { adminApi } from '../services/admin';
import { useAsync } from '../hooks/useAsync';
import { Eyebrow, EmptyState, Loading, Panel, Tag } from '../components/ui';
import { fmtInt, fmtDateTime } from '../lib/format';

/** `socks5://user@host:1080` — the endpoint without the password, which the API never sends. */
function endpointOf(p: AdminProxy): string {
  const auth = p.username ? `${p.username}@` : '';
  const port = p.port ? `:${p.port}` : '';
  return `${p.scheme}://${auth}${p.host}${port}`;
}

/** Flip one source in the list; the API stores the whole set. */
const toggle = (list: ProxyableSource[], src: ProxyableSource): ProxyableSource[] =>
  list.includes(src) ? list.filter((s) => s !== src) : [...list, src];

/** A never-used proxy has no timestamps yet — show a dash, not "Invalid Date". */
const when = (iso: string | null): string => (iso ? fmtDateTime(iso) : '—');

/** Health reads as one of four states; colour follows severity, not decoration. */
function health(p: AdminProxy): { label: string; tone: 'neutral' | 'saffron' | 'teal' } {
  if (!p.enabled) return { label: 'disabled', tone: 'neutral' };
  if (p.disabledUntil && new Date(p.disabledUntil) > new Date())
    return { label: 'cooling down', tone: 'saffron' };
  if (p.consecutiveFailures > 0) return { label: `${p.consecutiveFailures} failing`, tone: 'saffron' };
  return { label: 'ready', tone: 'teal' };
}

export function ProxiesPage() {
  const { data, error, loading, refresh } = useAsync(() => adminApi.proxies(), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [tests, setTests] = useState<Record<string, AdminProxyTestResult>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');

  async function act(id: string, fn: () => Promise<unknown>) {
    setBusy(id);
    setFormError(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(null);
    }
  }

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setBusy('new');
    setFormError(null);
    try {
      await adminApi.createProxy({ label: label.trim(), url: url.trim() });
      setLabel('');
      setUrl('');
      refresh();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not add proxy');
    } finally {
      setBusy(null);
    }
  }

  async function test(id: string) {
    setBusy(id);
    try {
      const result = await adminApi.testProxy(id);
      setTests((t) => ({ ...t, [id]: result }));
      refresh();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Probe failed');
    } finally {
      setBusy(null);
    }
  }

  const proxies = data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Eyebrow>Egress</Eyebrow>
        <h1 className="font-display text-2xl font-bold text-cream-100">Proxies</h1>
        <p className="mt-1 max-w-2xl text-sm text-cream-400">
          Outbound route for TikTok/Instagram video downloads. Datacenter IPs are blocked by both
          platforms, so downloads leave through these. With no proxies configured the worker
          downloads directly, which works from a residential IP but is expected to fail in
          production.
        </p>
      </div>

      <Panel>
        <form onSubmit={add} className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[0.62rem] uppercase tracking-wider text-cream-400">Label</span>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              placeholder="iproxy-de-1"
              className="w-48 rounded-lg border border-cream-600/20 bg-ink-850 px-3 py-2 text-sm text-cream-100 outline-none focus:border-cream-400/40"
            />
          </label>
          <label className="flex flex-1 flex-col gap-1">
            <span className="text-[0.62rem] uppercase tracking-wider text-cream-400">
              Proxy URL (stored write-only — never shown again)
            </span>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              placeholder="socks5://user:pass@host:1080"
              className="min-w-[18rem] rounded-lg border border-cream-600/20 bg-ink-850 px-3 py-2 font-mono text-sm text-cream-100 outline-none focus:border-cream-400/40"
            />
          </label>
          <button
            type="submit"
            disabled={busy === 'new'}
            className="rounded-lg bg-cream-100 px-4 py-2 text-sm font-semibold text-ink-900 disabled:opacity-50"
          >
            {busy === 'new' ? 'Adding…' : 'Add proxy'}
          </button>
        </form>
        {formError && <p className="mt-3 text-sm text-bad">{formError}</p>}
      </Panel>

      <Panel>
        {loading && <Loading />}
        {error && <p className="text-sm text-bad">{error}</p>}
        {!loading && !error && proxies.length === 0 && (
          <EmptyState
            title="No proxies configured"
            hint="Video import will download directly from the server's own IP."
          />
        )}
        {proxies.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-[0.62rem] uppercase tracking-wider text-cream-400">
                <tr>
                  <th className="py-2 pr-4">Label</th>
                  <th className="py-2 pr-4">Endpoint</th>
                  <th className="py-2 pr-4">Sources</th>
                  <th className="py-2 pr-4">Health</th>
                  <th className="py-2 pr-4">Last OK</th>
                  <th className="py-2 pr-4">Last fail</th>
                  <th className="py-2 pr-4 text-right">OK / fail</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {proxies.map((p) => {
                  const h = health(p);
                  const t = tests[p.id];
                  return (
                    <tr key={p.id} className="border-t border-cream-600/10 align-top">
                      <td className="py-3 pr-4 text-cream-100">{p.label}</td>
                      <td className="py-3 pr-4 font-mono text-xs text-cream-300">{endpointOf(p)}</td>
                      <td className="py-3 pr-4">
                        <div className="flex gap-1.5">
                          {PROXYABLE_SOURCES.map((src) => {
                            const on = p.sources.includes(src);
                            return (
                              <button
                                key={src}
                                onClick={() => act(p.id, () => adminApi.updateProxy(p.id, { sources: toggle(p.sources, src) }))}
                                disabled={busy === p.id}
                                title={on ? `Proxy IS used for ${src}` : `${src} goes direct`}
                                className={`rounded-md border px-2 py-0.5 text-xs disabled:opacity-50 ${
                                  on
                                    ? 'border-ok/40 bg-ok/10 text-ok'
                                    : 'border-cream-600/20 text-cream-500 line-through'
                                }`}
                              >
                                {src}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <Tag tone={h.tone}>{h.label}</Tag>
                        {t && (
                          <div className={`mt-1 text-xs ${t.ok ? 'text-ok' : 'text-bad'}`}>
                            {t.ok
                              ? `${t.latencyMs} ms · egress ${t.egressIp ?? 'unknown'}`
                              : (t.error ?? 'failed')}
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-cream-400">{when(p.lastOkAt)}</td>
                      <td className="py-3 pr-4 text-cream-400">{when(p.lastFailAt)}</td>
                      <td className="tnum py-3 pr-4 text-right text-cream-300">
                        {fmtInt(p.totalOk)} / {fmtInt(p.totalFail)}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => test(p.id)}
                            disabled={busy === p.id}
                            className="rounded-md border border-cream-600/20 px-2.5 py-1 text-xs text-cream-200 disabled:opacity-50"
                          >
                            Test
                          </button>
                          <button
                            onClick={() =>
                              act(p.id, () => adminApi.updateProxy(p.id, { enabled: !p.enabled }))
                            }
                            disabled={busy === p.id}
                            className="rounded-md border border-cream-600/20 px-2.5 py-1 text-xs text-cream-200 disabled:opacity-50"
                          >
                            {p.enabled ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete proxy "${p.label}"?`)) {
                                act(p.id, () => adminApi.deleteProxy(p.id));
                              }
                            }}
                            disabled={busy === p.id}
                            className="rounded-md border border-bad/30 px-2.5 py-1 text-xs text-bad disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
